import { createContext, useContext } from 'react';

import { Icon } from '../Icon.tsx';
import { Loader } from '../ui.tsx';
import * as wording from '../wording.ts';
import { type ActionDialog, primaryAction } from './actions.ts';
import type { CardMenuHost } from './CardMenu.tsx';
import type { BoardCard } from './model.ts';

/**
 * What the board page lends every card and the drawer: which cards have an
 * action running, and the one way to run one (which ignores a card that is
 * already running). Through context, so the columns and the lanes need not
 * hand it down card by card.
 */
export interface CardActions {
  readonly running: ReadonlySet<string>;
  run(card: BoardCard): void;
  /** Opens one of the board's dialogs on the card (the drawer's **Request changes**). */
  openDialog(dialog: ActionDialog, card: BoardCard): void;
  /** What each card's menu does (US-026). */
  readonly menu: CardMenuHost;
}

export const CardActionsContext = createContext<CardActions | null>(null);

/**
 * The card's primary action as a button (US-019): small on the card, where
 * CSS shows it on hover and focus, and `button--primary` under the drawer's
 * header. While it runs, a `Loader` stands in its place.
 */
export function PrimaryActionButton({ card, variant }: { readonly card: BoardCard; readonly variant: 'card' | 'drawer' }) {
  const actions = useContext(CardActionsContext);
  const action = primaryAction(card);
  if (actions === null || action === null) return null;
  const running = actions.running.has(card.cardId);

  if (variant === 'card' && running) {
    return (
      <div className="board-card__action board-card__action--running">
        <Loader size="sm" label={wording.ACTION_RUNNING} />
      </div>
    );
  }

  return (
    <div className={variant === 'card' ? 'board-card__action' : 'board-drawer__primary'}>
      <button
        type="button"
        className={variant === 'card' ? 'button button--small' : 'button button--primary'}
        disabled={running}
        aria-busy={running}
        onClick={() => {
          actions.run(card);
        }}
      >
        <Icon name={action.icon} />
        {action.label}
      </button>
      {running && <Loader size="sm" label={wording.ACTION_RUNNING} />}
    </div>
  );
}
